import { FEATURES, buildComponentViewEvent, logger, ConsoleLogSink, buildPageEvent, buildTrackEvent, buildIdentifyEvent, unionBy, pipe, PageviewProperties, Properties, Traits, isPageViewEvent, isTrackEvent, isIdentifyEvent, isComponentViewEvent, allowVariableTypeSchema, selectHasVariants, selectExperience, selectVariant as selectVariant$1, selectBaselineWithVariants, OnLogLogSink, OnErrorLogSink, NinetailedApiClient } from '@ninetailed/experience.js-shared';
export { EXPERIENCE_TRAIT_PREFIX, isExperienceMatch, selectActiveExperiments, selectDistribution, selectExperience, selectBaselineWithVariants as selectExperienceBaselineWithVariants, selectVariant as selectExperienceVariant, selectVariants as selectExperienceVariants, selectHasVariants as selectHasExperienceVariants } from '@ninetailed/experience.js-shared';
import { NinetailedAnalyticsPlugin, HAS_SEEN_COMPONENT, HAS_SEEN_VARIABLE, HAS_SEEN_ELEMENT, hasComponentViewTrackingThreshold } from '@ninetailed/experience.js-plugin-analytics';
import Analytics from 'analytics';
import { v4 } from 'uuid';

const COMPONENT = 'component';
const COMPONENT_START = 'componentStart';
const PAGE_HIDDEN = 'page_hidden';
const HAS_SEEN_STICKY_COMPONENT = 'sticky_component_view';

const buildClientLocale = () => navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;

const buildClientNinetailedRequestContext = () => ({
  url: window.location.href,
  referrer: document.referrer,
  locale: buildClientLocale(),
  userAgent: navigator.userAgent,
  document: {
    title: document.title
  }
});

/**
 * Similar to _.throttle but waits for the promise to resolve.
 * There is no "wait time" because you can simply await `Promise.timeout` inside `fn` to wait some time before the next call.
 */
function asyncThrottle(fn) {
  let runningPromise;
  let queuedPromise;
  let nextArgs;
  return async args => {
    if (runningPromise) {
      nextArgs = args;
      if (queuedPromise) {
        return queuedPromise;
      } else {
        queuedPromise = runningPromise.then(() => {
          queuedPromise = undefined;
          runningPromise = fn(nextArgs);
          return runningPromise;
        });
        return queuedPromise;
      }
    } else {
      runningPromise = fn(args);
      return runningPromise;
    }
  };
}

const LEGACY_ANONYMOUS_ID = '__anon_id';
const ANONYMOUS_ID = '__nt_anonymous_id__';
const DEBUG_FLAG = '__nt_debug__';
const PROFILE_FALLBACK_CACHE = '__nt_profile__';
const EXPERIENCES_FALLBACK_CACHE = '__nt_experiences__';
const CHANGES_FALLBACK_CACHE = '__nt_changes__';
const PROFILE_CHANGE = 'profile-change';
const PROFILE_RESET = 'profile-reset';
const CONSENT = '__nt-consent__';
const SET_ENABLED_FEATURES = 'setEnabledFeatures';
const EMPTY_MERGE_ID = 'nt:empty-merge-id';

const PLUGIN_NAME = 'ninetailed:core';

// eslint-disable-next-line @typescript-eslint/no-explicit-any

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
class NinetailedCorePlugin extends NinetailedAnalyticsPlugin {
  constructor({
    apiClient,
    locale,
    ninetailed,
    onInitProfileId,
    buildClientContext
  }) {
    var _this;
    super();
    _this = this;
    this.name = PLUGIN_NAME;
    this._instance = void 0;
    this.queue = [];
    this.enabledFeatures = Object.values(FEATURES);
    this.buildContext = buildClientNinetailedRequestContext;
    this.onInitProfileId = void 0;
    this.apiClient = void 0;
    this.locale = void 0;
    this.ninetailed = void 0;
    this[SET_ENABLED_FEATURES] = async function ({
      payload
    }) {
      _this.enabledFeatures = payload.features || [];
    };
    this.getComponentViewTrackingThreshold = () => {
      return 0;
    };
    this[HAS_SEEN_STICKY_COMPONENT] = async function ({
      payload
    }) {
      const ctx = _this.buildContext();
      return _this.enqueueEvent(buildComponentViewEvent({
        messageId: payload.meta.rid,
        timestamp: payload.meta.ts,
        componentId: payload.componentId,
        componentType: payload.componentType,
        experienceId: payload.experienceId,
        variantIndex: payload.variantIndex,
        ctx
      }));
    };
    this.methods = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      reset: async function (...args) {
        logger.debug('Resetting profile.');
        const instance = args[args.length - 1];
        instance.dispatch({
          type: PROFILE_RESET
        });
        _this.clearCaches();
        logger.debug('Removed old profile data from localstorage.');
        if (typeof _this.onInitProfileId === 'function') {
          const profileId = await _this.onInitProfileId(undefined);
          if (typeof profileId === 'string') {
            _this.setAnonymousId(profileId);
          }
        }
        await _this.ninetailed.track('nt_reset');
        logger.info('Profile reset successful.');
        await delay(10);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      debug: async function (...args) {
        const enabled = args[0];
        const instance = args[args.length - 1];
        const consoleLogSink = new ConsoleLogSink();
        if (enabled) {
          instance.storage.setItem(DEBUG_FLAG, true);
          logger.addSink(consoleLogSink);
          logger.info('Debug mode enabled.');
        } else {
          instance.storage.removeItem(DEBUG_FLAG);
          logger.info('Debug mode disabled.');
          logger.removeSink(consoleLogSink.name);
        }
      }
    };
    this.flush = asyncThrottle(this._flush.bind(this));
    if (onInitProfileId) {
      this.onInitProfileId = onInitProfileId;
    }
    if (buildClientContext) {
      this.buildContext = buildClientContext;
    }
    this.apiClient = apiClient;
    this.ninetailed = ninetailed;
    this.locale = locale;
  }
  async initialize({
    instance
  }) {
    this._instance = instance;
    if (instance.storage.getItem(DEBUG_FLAG)) {
      logger.addSink(new ConsoleLogSink());
      logger.info('Ninetailed Debug Mode is enabled.');
    }

    // legacy support for the old anonymousId
    const legacyAnonymousId = instance.storage.getItem(LEGACY_ANONYMOUS_ID);
    if (legacyAnonymousId) {
      logger.debug('Found legacy anonymousId, migrating to new one.', legacyAnonymousId);
      this.setAnonymousId(legacyAnonymousId);
      instance.storage.removeItem(LEGACY_ANONYMOUS_ID);
    }
    if (typeof this.onInitProfileId === 'function') {
      const profileId = await this.onInitProfileId(this.getAnonymousId());
      if (typeof profileId === 'string') {
        this.setAnonymousId(profileId);
      }
    }
    logger.debug('Ninetailed Core plugin initialized.');
  }
  pageStart(params) {
    return NinetailedCorePlugin.abortNonClientEvents(params);
  }
  async page({
    payload
  }) {
    logger.info('Sending Page event.');
    const ctx = this.buildContext();
    return this.enqueueEvent(buildPageEvent({
      messageId: payload.meta.rid,
      timestamp: payload.meta.ts,
      properties: payload.properties,
      ctx
    }));
  }
  trackStart(params) {
    return NinetailedCorePlugin.abortNonClientEvents(params);
  }
  async track({
    payload
  }) {
    logger.info('Sending Track event.');
    const ctx = this.buildContext();
    return this.enqueueEvent(buildTrackEvent({
      messageId: payload.meta.rid,
      timestamp: payload.meta.ts,
      event: payload.event,
      properties: payload.properties,
      ctx
    }));
  }
  identifyStart(params) {
    return NinetailedCorePlugin.abortNonClientEvents(params);
  }
  async identify({
    payload
  }) {
    logger.info('Sending Identify event.');
    const ctx = this.buildContext();
    if (payload.userId === EMPTY_MERGE_ID && (!payload.traits || typeof payload.traits === 'object' && Object.keys(payload.traits).length === 0)) {
      logger.info('Skipping Identify event as no userId and no traits are set.');
      return;
    }
    return this.enqueueEvent(buildIdentifyEvent({
      messageId: payload.meta.rid,
      timestamp: payload.meta.ts,
      traits: payload.traits,
      userId: payload.userId === EMPTY_MERGE_ID ? '' : payload.userId,
      ctx
    }));
  }
  async onTrackExperience(properties) {
    if (properties.experience.sticky) {
      await this.instance.dispatch({
        type: HAS_SEEN_STICKY_COMPONENT,
        componentId: properties.selectedVariant.id,
        experienceId: properties.experience.id,
        variantIndex: properties.selectedVariantIndex
      });
      this.flush();
    }
    return Promise.resolve();
  }
  onTrackComponent() {
    return Promise.resolve();
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setItemStart({
    abort,
    payload
  }) {
    if (![ANONYMOUS_ID, DEBUG_FLAG, PROFILE_FALLBACK_CACHE, EXPERIENCES_FALLBACK_CACHE, CONSENT].includes(payload.key)) {
      return abort();
    }
    return payload;
  }
  async enqueueEvent(event) {
    this.queue = unionBy([event], this.queue, 'messageId');
  }
  static abortNonClientEvents({
    abort,
    payload
  }) {
    if (typeof window !== 'object') {
      return abort();
    }
    return payload;
  }
  get instance() {
    if (!this._instance) {
      throw new Error('Ninetailed Core plugin not initialized.');
    }
    return this._instance;
  }
  getAnonymousId() {
    var _ref;
    return (_ref = this.instance.storage.getItem(ANONYMOUS_ID)) != null ? _ref : undefined;
  }
  setAnonymousId(id) {
    this.instance.storage.setItem(ANONYMOUS_ID, id);
  }
  clearAnonymousId() {
    this.instance.storage.removeItem(ANONYMOUS_ID);
  }
  setFallbackProfile(profile) {
    this.instance.storage.setItem(PROFILE_FALLBACK_CACHE, profile);
  }
  getFallbackProfile() {
    var _ref2;
    return (_ref2 = this.instance.storage.getItem(PROFILE_FALLBACK_CACHE)) != null ? _ref2 : undefined;
  }
  clearFallbackProfile() {
    this.instance.storage.removeItem(PROFILE_FALLBACK_CACHE);
  }
  setFallbackExperiences(experiences) {
    this.instance.storage.setItem(EXPERIENCES_FALLBACK_CACHE, experiences);
  }
  getFallbackExperiences() {
    return this.instance.storage.getItem(EXPERIENCES_FALLBACK_CACHE) || [];
  }
  clearFallbackExperiences() {
    this.instance.storage.removeItem(EXPERIENCES_FALLBACK_CACHE);
  }
  setFallbackChanges(changes) {
    this.instance.storage.setItem(CHANGES_FALLBACK_CACHE, changes);
  }
  getFallbackChanges() {
    return this.instance.storage.getItem(CHANGES_FALLBACK_CACHE) || [];
  }
  clearFallbackChanges() {
    this.instance.storage.removeItem(CHANGES_FALLBACK_CACHE);
  }
  clearCaches() {
    this.clearAnonymousId();
    this.clearFallbackProfile();
    this.clearFallbackExperiences();
    this.clearFallbackChanges();
  }
  populateCaches({
    experiences,
    profile,
    anonymousId,
    changes
  }) {
    this.setAnonymousId(anonymousId);
    this.setFallbackProfile(profile);
    this.setFallbackExperiences(experiences);
    this.setFallbackChanges(changes);
  }
  async _flush() {
    const events = Object.assign([], this.queue);
    logger.info('Start flushing events.');
    this.queue = [];
    if (!events.length) {
      return {
        success: true
      };
    }
    try {
      const anonymousId = this.getAnonymousId();
      const {
        profile,
        experiences,
        changes
      } = await this.apiClient.upsertProfile({
        profileId: anonymousId,
        events
      }, {
        locale: this.locale,
        enabledFeatures: this.enabledFeatures
      });
      this.populateCaches({
        anonymousId: profile.id,
        profile,
        experiences,
        changes
      });
      logger.debug('Profile from api: ', profile);
      logger.debug('Experiences from api: ', experiences);
      this.instance.dispatch({
        type: PROFILE_CHANGE,
        profile,
        experiences,
        changes,
        error: undefined
      });
      await delay(20);
      return {
        success: true
      };
    } catch (error) {
      logger.debug('An error occurred during flushing the events: ', error);
      const fallbackProfile = this.getFallbackProfile();
      const fallbackExperiences = this.getFallbackExperiences();
      const fallbackChanges = this.getFallbackChanges();
      if (fallbackProfile) {
        logger.debug('Found a fallback profile - will use this.');
        this.instance.dispatch({
          type: PROFILE_CHANGE,
          profile: fallbackProfile,
          experiences: fallbackExperiences,
          changes: fallbackChanges,
          error: undefined
        });
      } else {
        logger.debug('No fallback profile found - setting profile to null.');
        this.instance.dispatch({
          type: PROFILE_CHANGE,
          profile: null,
          changes: fallbackChanges,
          experiences: fallbackExperiences,
          error: error
        });
      }
      return {
        success: false
      };
    }
  }
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

class ElementSeenObserver {
  constructor(_options) {
    this._intersectionObserver = void 0;
    this._elementDelays = void 0;
    this._intersectionTimers = void 0;
    this._options = _options;
    this._elementDelays = new WeakMap();
    this._intersectionTimers = new WeakMap();
    if (typeof IntersectionObserver !== 'undefined') {
      this._intersectionObserver = new IntersectionObserver(this.onIntersection.bind(this));
    }
  }
  onIntersection(entries) {
    entries.forEach(entry => {
      const {
        isIntersecting,
        target
      } = entry;
      if (isIntersecting) {
        const delays = this._elementDelays.get(target);
        delays == null || delays.forEach(delay => {
          const timeOut = window.setTimeout(() => {
            this._options.onElementSeen(target, delay);
          }, delay);
          const currentTimers = this._intersectionTimers.get(target) || [];
          this._intersectionTimers.set(target, [...currentTimers, timeOut]);
        });
      } else {
        const timeOuts = this._intersectionTimers.get(target);
        timeOuts == null || timeOuts.forEach(timeOut => {
          if (typeof timeOut !== 'undefined') {
            window.clearTimeout(timeOut);
          }
        });
      }
    });
  }
  observe(element, options) {
    var _this$_intersectionOb;
    const delays = this._elementDelays.get(element) || [];
    this._elementDelays.set(element, Array.from(new Set([...delays, (options == null ? void 0 : options.delay) || 0])));
    (_this$_intersectionOb = this._intersectionObserver) == null || _this$_intersectionOb.observe(element);
  }
  unobserve(element) {
    var _this$_intersectionOb2;
    (_this$_intersectionOb2 = this._intersectionObserver) == null || _this$_intersectionOb2.unobserve(element);
  }
}

const acceptsCredentials = plugin => {
  return typeof plugin === 'object' && plugin !== null && 'setCredentials' in plugin && typeof plugin.setCredentials === 'function';
};

const isInterestedInHiddenPage = arg => {
  return typeof arg === 'object' && arg !== null && PAGE_HIDDEN in arg && typeof arg[PAGE_HIDDEN] === 'function';
};

const decodeExperienceVariantsMap = encodedExperienceVariantsMap => {
  const experientVariantsAssignments = encodedExperienceVariantsMap.split(',');
  const experienceVariantsMap = {};
  for (const experienceVariantAssignment of experientVariantsAssignments) {
    const [experienceId, variantIndexString] = experienceVariantAssignment.split('=');
    const variantIndex = parseInt(variantIndexString);
    if (!experienceId || !variantIndex) {
      continue;
    }
    experienceVariantsMap[experienceId] = variantIndex;
  }
  return experienceVariantsMap;
};

class OnChangeEmitter {
  constructor() {
    this.onChangeListeners = [];
  }
  addListener(listener) {
    this.onChangeListeners.push(listener);
    return () => {
      this.removeOnChangeListener(listener);
    };
  }
  invokeListeners() {
    this.onChangeListeners.forEach(listener => listener());
  }
  removeOnChangeListener(listener) {
    this.onChangeListeners = this.onChangeListeners.filter(l => l !== listener);
  }
}

const hasOnChangeEmitter = arg => {
  return typeof arg === 'object' && arg !== null && 'onChangeEmitter' in arg && typeof arg.onChangeEmitter === 'object' && arg.onChangeEmitter !== null && arg.onChangeEmitter.constructor === OnChangeEmitter;
};

const selectPluginsHavingOnChangeEmitter = plugins => {
  const filteredPlugins = [];
  for (const plugin of plugins) {
    if (hasOnChangeEmitter(plugin)) {
      filteredPlugins.push(plugin);
    }
  }
  return filteredPlugins;
};

const hasExperienceSelectionMiddleware = arg => {
  return typeof arg === 'object' && arg !== null && 'getExperienceSelectionMiddleware' in arg && typeof arg.getExperienceSelectionMiddleware === 'function';
};

const selectPluginsHavingExperienceSelectionMiddleware = plugins => {
  const filteredPlugins = [];
  for (const plugin of plugins) {
    if (hasExperienceSelectionMiddleware(plugin)) {
      filteredPlugins.push(plugin);
    }
  }
  return filteredPlugins;
};

const createPassThroughMiddleware$1 = () => {
  return ({
    experience,
    variant,
    variantIndex
  }) => {
    return {
      experience,
      variant,
      variantIndex
    };
  };
};
function createExperienceSelectionMiddleware({
  plugins,
  experiences,
  baseline,
  profile
}) {
  if (profile === null) {
    return createPassThroughMiddleware$1();
  }
  const pluginsWithMiddleware = selectPluginsHavingExperienceSelectionMiddleware(plugins);
  const middlewareFunctions = [];
  for (const plugin of pluginsWithMiddleware) {
    const middleware = plugin.getExperienceSelectionMiddleware({
      experiences,
      baseline
    });
    if (middleware !== undefined) {
      middlewareFunctions.push(middleware);
    }
  }
  return pipe(...middlewareFunctions);
}
const makeExperienceSelectMiddleware = ({
  plugins,
  onChange,
  experiences,
  baseline,
  profile
}) => {
  let removeChangeListeners = [];
  const pluginsHavingChangeEmitters = selectPluginsHavingOnChangeEmitter(plugins);
  const middleware = createExperienceSelectionMiddleware({
    plugins,
    experiences,
    baseline,
    profile
  });
  const addListeners = () => {
    removeChangeListeners = pluginsHavingChangeEmitters.map(plugin => {
      const listener = () => {
        onChange(middleware);
      };
      return plugin.onChangeEmitter.addListener(listener);
    });
  };

  // WARNING: This specific implementation using forEach is required.
  // DO NOT replace with for...of or other loop constructs as they will break functionality.
  // The exact reason is uncertain but appears related to the transplier.
  // TODO: Come back and find out why this is the case, maybe a version bump is in order.
  const removeListeners = () => {
    removeChangeListeners.forEach(removeListener => removeListener());
  };
  return {
    addListeners,
    removeListeners,
    middleware
  };
};

/**
 * Type guard that checks if an object implements the HasChangesModificationMiddleware interface
 *
 * @param arg Object to check
 * @returns Boolean indicating if the object implements HasChangesModificationMiddleware
 */
const hasChangesModificationMiddleware = arg => {
  return typeof arg === 'object' && arg !== null && 'getChangesModificationMiddleware' in arg && typeof arg.getChangesModificationMiddleware === 'function';
};

/**
 * Selects plugins that implement the HasChangesModificationMiddleware interface
 *
 * @param plugins Array of Ninetailed plugins
 * @returns Array of plugins that implement HasChangesModificationMiddleware
 */
const selectPluginsHavingChangesModificationMiddleware = plugins => {
  const filteredPlugins = [];
  for (const plugin of plugins) {
    if (hasChangesModificationMiddleware(plugin)) {
      filteredPlugins.push(plugin);
    }
  }
  return filteredPlugins;
};

/**
 * Creates a pass-through middleware that doesn't modify changes
 */
const createPassThroughMiddleware = () => {
  return ({
    changes
  }) => {
    return {
      changes
    };
  };
};

/**
 * Creates a middleware function by composing middleware from multiple plugins
 */
function createChangesModificationMiddleware({
  plugins,
  changes
}) {
  const pluginsWithMiddleware = selectPluginsHavingChangesModificationMiddleware(plugins);
  const middlewareFunctions = [];
  for (const plugin of pluginsWithMiddleware) {
    const middleware = plugin.getChangesModificationMiddleware({
      changes
    });
    if (middleware !== undefined) {
      middlewareFunctions.push(middleware);
    }
  }

  // If no middleware functions were found, return a pass-through middleware
  if (middlewareFunctions.length === 0) {
    return createPassThroughMiddleware();
  }

  // Compose middleware functions using pipe
  return pipe(...middlewareFunctions);
}

/**
 * Creates a changes modification middleware system with change notification
 *
 * This follows the same pattern as the experience selection middleware system
 */
const makeChangesModificationMiddleware = ({
  plugins,
  onChange,
  changes
}) => {
  let removeChangeListeners = [];
  const pluginsHavingChangeEmitters = selectPluginsHavingOnChangeEmitter(plugins);
  const middleware = createChangesModificationMiddleware({
    plugins,
    changes
  });
  const addListeners = () => {
    removeChangeListeners = pluginsHavingChangeEmitters.map(plugin => {
      const listener = () => {
        // When a plugin changes, recreate the middleware and notify
        const updatedMiddleware = createChangesModificationMiddleware({
          plugins,
          changes
        });
        onChange(updatedMiddleware);
      };
      return plugin.onChangeEmitter.addListener(listener);
    });
  };

  // WARNING: This specific implementation using forEach is required.
  // DO NOT replace with for...of or other loop constructs as they will break functionality.
  // The exact reason is uncertain but appears related to the transpiler.
  const removeListeners = () => {
    removeChangeListeners.forEach(removeListener => removeListener());
  };
  return {
    addListeners,
    removeListeners,
    middleware
  };
};

class EventBuilder {
  constructor(buildRequestContext) {
    this.buildRequestContext = void 0;
    this.buildRequestContext = buildRequestContext || buildClientNinetailedRequestContext;
  }
  buildEventBase(data) {
    return Object.assign({
      messageId: (data == null ? void 0 : data.messageId) || v4()
    }, data, {
      timestamp: Date.now(),
      ctx: this.buildRequestContext()
    });
  }
  page(properties, data) {
    return buildPageEvent(Object.assign({}, this.buildEventBase(data), {
      properties: properties || {}
    }));
  }
  track(event, properties, data) {
    return buildTrackEvent(Object.assign({}, this.buildEventBase(data), {
      event,
      properties: properties || {}
    }));
  }
  identify(userId, traits, data) {
    return buildIdentifyEvent(Object.assign({}, this.buildEventBase(data), {
      traits: traits || {},
      userId: userId || ''
    }));
  }
  component(componentId, componentType, experienceId, variantIndex, data) {
    return buildComponentViewEvent(Object.assign({}, this.buildEventBase(data), {
      componentId,
      componentType: componentType || 'Entry',
      experienceId: experienceId || '',
      variantIndex: variantIndex || 0
    }));
  }
}

const requiresEventBuilder = plugin => {
  return typeof plugin === 'object' && plugin !== null && 'setEventBuilder' in plugin && typeof plugin.setEventBuilder === 'function';
};

const _excluded = ["experience", "variant", "variantIndex"],
  _excluded2 = ["element"];
const buildOverrideMiddleware = experienceSelectionMiddleware => _ref => {
  let {
      experience: originalExperience,
      variant: originalVariant,
      variantIndex: originalVariantIndex
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    experience,
    variant,
    variantIndex
  } = experienceSelectionMiddleware({
    experience: originalExperience,
    variant: originalVariant,
    variantIndex: originalVariantIndex
  });
  return Object.assign({}, other, {
    audience: experience != null && experience.audience ? experience.audience : null,
    experience,
    variant,
    variantIndex
  });
};
class Ninetailed {
  constructor(ninetailedApiClientInstanceOrOptions, {
    plugins,
    url,
    locale,
    requestTimeout,
    onLog,
    onError,
    buildClientContext,
    onInitProfileId,
    componentViewTrackingThreshold = 2000,
    storageImpl,
    useSDKEvaluation = false
  } = {}) {
    var _this = this;
    this.instance = void 0;
    this._profileState = void 0;
    this.isInitialized = false;
    this.apiClient = void 0;
    this.ninetailedCorePlugin = void 0;
    this.elementSeenObserver = void 0;
    this.observedElements = void 0;
    this.clientId = void 0;
    this.environment = void 0;
    this.plugins = void 0;
    this.logger = void 0;
    this.componentViewTrackingThreshold = void 0;
    this.useSDKEvaluation = void 0;
    this.eventBuilder = void 0;
    this.page = async function (data, options) {
      try {
        const result = PageviewProperties.partial().default({}).safeParse(data);
        if (!result.success) {
          throw new Error(`[Validation Error] "page" was called with invalid params. Page data is not valid: ${result.error.format()}`);
        }
        await _this.waitUntilInitialized();
        await _this.instance.page(data, _this.buildOptions(options));
        return _this.ninetailedCorePlugin.flush();
      } catch (error) {
        return _this.handleMethodError(error, 'page');
      }
    };
    this.track = async function (event, properties, options) {
      try {
        const result = Properties.default({}).safeParse(properties);
        if (!result.success) {
          throw new Error(`[Validation Error] "track" was called with invalid params. Properties are no valid json object: ${result.error.format()}`);
        }
        await _this.waitUntilInitialized();
        await _this.instance.track(event.toString(), result.data, _this.buildOptions(options));
        return _this.ninetailedCorePlugin.flush();
      } catch (error) {
        _this.handleMethodError(error, 'track');
      }
    };
    this.identify = async function (uid, traits, options) {
      try {
        const result = Traits.default({}).safeParse(traits);
        if (!result.success) {
          throw new Error(`[Validation Error] "identify" was called with invalid params. Traits are no valid json: ${result.error.format()}`);
        }
        await _this.waitUntilInitialized();
        await _this.instance.identify(uid && uid.toString() !== '' ? uid.toString() : EMPTY_MERGE_ID, result.data, _this.buildOptions(options));
        return _this.ninetailedCorePlugin.flush();
      } catch (error) {
        _this.handleMethodError(error, 'identify');
      }
    };
    this.batch = async function (events) {
      try {
        await _this.waitUntilInitialized();
        const promises = events.map(event => {
          if (isPageViewEvent(event)) {
            return _this.instance.page(event.properties);
          }
          if (isTrackEvent(event)) {
            return _this.instance.track(event.event, event.properties);
          }
          if (isIdentifyEvent(event)) {
            return _this.instance.identify(event.userId || EMPTY_MERGE_ID, event.traits);
          }
          if (isComponentViewEvent(event)) {
            return _this.instance.dispatch({
              experienceId: event.experienceId,
              componentId: event.componentId,
              variantIndex: event.variantIndex,
              type: HAS_SEEN_STICKY_COMPONENT
            });
          }
          return Promise.resolve();
        });
        await Promise.all(promises);
        return _this.ninetailedCorePlugin.flush();
      } catch (error) {
        _this.handleMethodError(error, 'batch');
      }
    };
    this.trackStickyComponentView = async function ({
      experienceId,
      componentId,
      variantIndex
    }) {
      try {
        await _this.waitUntilInitialized();
        await _this.instance.dispatch({
          experienceId,
          componentId,
          variantIndex,
          type: HAS_SEEN_STICKY_COMPONENT
        });
        return _this.ninetailedCorePlugin.flush();
      } catch (error) {
        _this.handleMethodError(error, 'trackStickyComponentView');
      }
    };
    /**
     * @deprecated The legacy datamodel is not recommended anymore
     * Will be removed in the next version of the SDK
     */
    this.trackHasSeenComponent = async function (properties) {
      return _this.instance.dispatch(Object.assign({}, properties, {
        type: HAS_SEEN_COMPONENT
      }));
    };
    this.trackVariableComponentView = properties => {
      const validatedVariable = allowVariableTypeSchema.parse(properties.variable);
      return this.instance.dispatch(Object.assign({}, properties, {
        type: HAS_SEEN_VARIABLE,
        variable: validatedVariable
      }));
    };
    this.trackComponentView = properties => {
      return this.instance.dispatch(Object.assign({}, properties, {
        type: HAS_SEEN_ELEMENT,
        // TODO this is a temporary solution - to not make the user specify a delay
        seenFor: this.componentViewTrackingThreshold
      }));
    };
    this.observeElement = (payload, options) => {
      const {
          element
        } = payload,
        remainingPayload = _objectWithoutPropertiesLoose(payload, _excluded2);
      if (!(element instanceof Element)) {
        this.logInvalidElement(element);
        return;
      }
      this.storeElementPayload(element, remainingPayload);
      this.setupElementObservation(element, options == null ? void 0 : options.delay);
    };
    this.unobserveElement = element => {
      this.observedElements.delete(element);
      this.elementSeenObserver.unobserve(element);
    };
    this.onElementSeen = (element, delay) => {
      const payloads = this.observedElements.get(element);
      if (Array.isArray(payloads) && payloads.length > 0) {
        for (const payload of payloads) {
          this.instance.dispatch(Object.assign({}, payload, {
            element,
            type: HAS_SEEN_ELEMENT,
            seenFor: delay
          }));
        }
      }
    };
    this.reset = async function () {
      await _this.waitUntilInitialized();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      _this.instance.plugins[PLUGIN_NAME].reset();
    };
    this.debug = async function (enabled) {
      await _this.waitUntilInitialized();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      _this.instance.plugins[PLUGIN_NAME].debug(enabled);
    };
    this.onProfileChange = cb => {
      cb(this.profileState);
      return this.instance.on(PROFILE_CHANGE, ({
        payload
      }) => {
        if (payload.error) {
          cb(Object.assign({}, this._profileState, {
            status: 'error',
            profile: payload.profile,
            experiences: payload.experiences,
            error: payload.error
          }));
        } else {
          cb(Object.assign({}, this._profileState, {
            status: 'success',
            profile: payload.profile,
            changes: payload.changes,
            experiences: payload.experiences,
            error: null
          }));
        }
      });
    };
    this.onSelectVariant = ({
      baseline,
      experiences
    }, cb) => {
      let middlewareChangeListeners = [];
      let state = null;
      const removeMiddlewareChangeListeners = () => {
        middlewareChangeListeners.forEach(removeListener => removeListener());
        middlewareChangeListeners = [];
      };
      const setSelectedVariant = newState => {
        state = newState;
        cb(state);
      };
      const removeProfileChangeListener = this.onProfileChange(profileState => {
        const {
          addListeners,
          removeListeners,
          middleware: experienceSelectionMiddleware
        } = makeExperienceSelectMiddleware({
          plugins: this.plugins,
          experiences,
          baseline,
          profile: profileState.profile,
          onChange: middleware => {
            const overrideResult = buildOverrideMiddleware(middleware);
            if (state !== null) {
              setSelectedVariant(overrideResult(state));
            }
          }
        });
        addListeners();
        middlewareChangeListeners.push(removeListeners);
        const overrideResult = buildOverrideMiddleware(experienceSelectionMiddleware);
        const hasVariants = experiences.map(experience => selectHasVariants(experience, baseline)).reduce((acc, curr) => acc || curr, false);
        const baseReturn = Object.assign({}, profileState, {
          hasVariants,
          baseline
        });
        const emptyReturn = Object.assign({}, baseReturn, {
          experience: null,
          variant: baseline,
          variantIndex: 0,
          audience: null,
          isPersonalized: false,
          profile: null,
          error: null
        });
        if (profileState.status === 'loading') {
          setSelectedVariant(overrideResult(Object.assign({}, emptyReturn, {
            loading: true,
            status: 'loading'
          })));
          return;
        }
        if (profileState.status === 'error') {
          setSelectedVariant(overrideResult(Object.assign({}, emptyReturn, {
            loading: false,
            status: 'error',
            error: profileState.error
          })));
          return;
        }
        const {
          profile,
          experiences: selectedExperiences
        } = profileState;
        if (!profile || !selectedExperiences) {
          setSelectedVariant(overrideResult(Object.assign({}, emptyReturn, {
            loading: false,
            status: 'error',
            error: new Error('No Profile or Selected Experiences were returned by the API')
          })));
          return;
        }
        if (this.useSDKEvaluation) {
          const _experience = selectExperience({
            experiences,
            profile
          });
          if (!_experience) {
            setSelectedVariant(overrideResult(Object.assign({}, emptyReturn, {
              loading: false,
              status: 'success',
              profile
            })));
            return;
          }
          const {
            variant: _variant,
            index
          } = selectVariant$1({
            baseline,
            experience: _experience,
            profile
          });
          setSelectedVariant(overrideResult(Object.assign({}, baseReturn, {
            status: 'success',
            loading: false,
            error: null,
            experience: _experience,
            variant: _variant,
            variantIndex: index,
            audience: _experience.audience ? _experience.audience : null,
            profile,
            isPersonalized: true
          })));
          return;
        }
        const experience = experiences.find(experience => selectedExperiences.some(selectedExperience => selectedExperience.experienceId === experience.id));
        const selectedExperience = selectedExperiences.find(({
          experienceId
        }) => experienceId === (experience == null ? void 0 : experience.id));
        if (!experience || !selectedExperience) {
          setSelectedVariant(overrideResult(Object.assign({}, emptyReturn, {
            loading: false,
            status: 'success',
            profile
          })));
          return;
        }
        const experienceWithStickyFromExperienceApi = Object.assign({}, experience, {
          sticky: selectedExperience.sticky
        });
        const baselineVariants = selectBaselineWithVariants(experienceWithStickyFromExperienceApi, baseline);
        if (!baselineVariants) {
          setSelectedVariant(overrideResult(Object.assign({}, emptyReturn, {
            loading: false,
            status: 'success',
            profile
          })));
          return;
        }
        const {
          variants
        } = baselineVariants;
        const variant = [baseline, ...variants][selectedExperience.variantIndex];
        if (!variant) {
          setSelectedVariant(overrideResult(Object.assign({}, emptyReturn, {
            loading: false,
            status: 'success',
            profile
          })));
          return;
        }
        setSelectedVariant(overrideResult(Object.assign({}, baseReturn, {
          status: 'success',
          loading: false,
          error: null,
          experience: experienceWithStickyFromExperienceApi,
          variant,
          variantIndex: selectedExperience.variantIndex,
          audience: experience.audience ? experience.audience : null,
          profile,
          isPersonalized: true
        })));
      });
      return () => {
        removeProfileChangeListener();
        removeMiddlewareChangeListeners();
      };
    };
    /**
     * Registers a callback to be notified when changes occur in the profile state.
     * Uses the changes modification middleware system to process changes.
     * Changes are processed in the following order:
     *
     * @param cb - Callback function that receives the changes state
     * @returns Function to unsubscribe from changes updates
     */
    this.onChangesChange = cb => {
      // Initially notify with current state
      this.notifyChangesCallback(cb, this._profileState);
      let middlewareChangeListeners = [];
      const removeProfileChangeListener = this.onProfileChange(profileState => {
        // Clean up any existing middleware listeners
        middlewareChangeListeners.forEach(removeListener => removeListener());
        middlewareChangeListeners = [];

        // If we're in loading or error state, simply pass through
        if (profileState.status !== 'success') {
          this.notifyChangesCallback(cb, profileState);
          return;
        }

        // Set up middleware for changes
        const {
          addListeners,
          removeListeners,
          middleware: changesModificationMiddleware
        } = makeChangesModificationMiddleware({
          plugins: this.plugins,
          changes: profileState.changes || [],
          onChange: updatedMiddleware => {
            // When plugin state changes, reapply middleware to current changes
            if (profileState.status === 'success' && profileState.changes) {
              const {
                changes: modifiedChanges
              } = updatedMiddleware({
                changes: profileState.changes
              });
              cb({
                status: 'success',
                changes: modifiedChanges,
                error: null
              });
            }
          }
        });

        // Add listeners for plugin changes
        addListeners();
        middlewareChangeListeners.push(removeListeners);

        // Apply middleware to current changes
        if (profileState.changes) {
          const {
            changes: modifiedChanges
          } = changesModificationMiddleware({
            changes: profileState.changes
          });
          cb({
            status: 'success',
            changes: modifiedChanges,
            error: null
          });
        } else {
          cb({
            status: 'success',
            changes: [],
            error: null
          });
        }
      });

      // Return a function that cleans up all listeners
      return () => {
        removeProfileChangeListener();
        middlewareChangeListeners.forEach(removeListener => removeListener());
      };
    };
    this.onIsInitialized = onIsInitializedCallback => {
      if (typeof onIsInitializedCallback === 'function') {
        if (this.isInitialized) {
          onIsInitializedCallback();
        } else {
          const detachOnReadyListener = this.instance.on('ready', () => {
            this.isInitialized = true;
            onIsInitializedCallback();
            detachOnReadyListener();
          });
        }
      }
    };
    this.waitUntilInitialized = () => {
      return new Promise(resolve => {
        this.onIsInitialized(resolve);
      });
    };
    // The following methods are used to register methods of the Ninetailed instance on the window object.
    // GTM templates do not support executing async functions in `callInWindow`.
    // Therefore, we provide a versions of those methods without the async keyword.
    this.trackAsWindowHandler = (event, properties, options) => {
      return this.track(event, properties, options);
    };
    this.identifyAsWindowHandler = (uid, traits, options) => {
      return this.identify(uid, traits, options);
    };
    this.pageAsWindowHandler = (data, options) => {
      return this.page(data, options);
    };
    this.onVisibilityChange = () => {
      if (typeof document === 'undefined') {
        return;
      }
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.instance.dispatch({
            type: PAGE_HIDDEN
          });
        }
      });
    };
    if (typeof onLog === 'function') {
      logger.addSink(new OnLogLogSink(onLog));
    }
    if (typeof onError === 'function') {
      logger.addSink(new OnErrorLogSink(onError));
    }
    this.logger = logger;
    this.useSDKEvaluation = useSDKEvaluation;
    if (ninetailedApiClientInstanceOrOptions instanceof NinetailedApiClient) {
      this.apiClient = ninetailedApiClientInstanceOrOptions;
    } else {
      const {
        clientId,
        environment,
        preview
      } = ninetailedApiClientInstanceOrOptions;
      this.clientId = clientId;
      this.environment = environment || 'main';
      this.apiClient = new NinetailedApiClient({
        clientId,
        environment,
        url,
        preview
      });
    }
    this.plugins = (plugins != null ? plugins : []).flat();
    this.eventBuilder = new EventBuilder(buildClientContext);
    this.plugins.forEach(plugin => {
      if (acceptsCredentials(plugin) && this.clientId && this.environment) {
        plugin.setCredentials({
          clientId: this.clientId,
          environment: this.environment
        });
      }
      if (hasComponentViewTrackingThreshold(plugin)) {
        plugin.setComponentViewTrackingThreshold(componentViewTrackingThreshold);
      }
      if (requiresEventBuilder(plugin)) {
        plugin.setEventBuilder(this.eventBuilder);
      }
    });
    this._profileState = {
      status: 'loading',
      profile: null,
      experiences: null,
      changes: null,
      error: null,
      from: 'api'
    };
    this.ninetailedCorePlugin = new NinetailedCorePlugin({
      apiClient: this.apiClient,
      locale,
      requestTimeout,
      buildClientContext,
      onInitProfileId,
      ninetailed: this
    });
    this.instance = Analytics(Object.assign({
      app: 'ninetailed',
      plugins: [...this.plugins, this.ninetailedCorePlugin]
    }, storageImpl ? {
      storage: storageImpl
    } : {}));
    const _detachOnReadyListener = this.instance.on('ready', () => {
      this.isInitialized = true;
      logger.info('Ninetailed Experience.js SDK is completely initialized.');
      _detachOnReadyListener();
    });

    // put in private method
    this.onProfileChange(profileState => {
      this._profileState = profileState;
      if (typeof window !== 'undefined') {
        window.ninetailed = Object.assign({}, window.ninetailed, {
          profile: this.profileState.profile,
          experiences: this.profileState.experiences
        });
      }
    });
    this.observedElements = new WeakMap();
    this.elementSeenObserver = new ElementSeenObserver({
      onElementSeen: this.onElementSeen.bind(this)
    });
    this.componentViewTrackingThreshold = componentViewTrackingThreshold;
    const hasPluginsInterestedInHiddenPage = this.plugins.some(isInterestedInHiddenPage);
    if (hasPluginsInterestedInHiddenPage) {
      this.onVisibilityChange();
    }
    this.registerWindowHandlers();
  }
  get pluginsWithCustomComponentViewThreshold() {
    return [this.ninetailedCorePlugin, ...this.plugins].filter(plugin => hasComponentViewTrackingThreshold(plugin));
  }
  logInvalidElement(element) {
    const isObject = typeof element === 'object' && element !== null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const constructorName = isObject ? element.constructor.name : '';
    const isConstructorNameNotObject = constructorName && constructorName !== 'Object';
    logger.warn(`ElementSeenObserver.observeElement was called with an invalid element. Expected an Element but got ${typeof element}${isConstructorNameNotObject ? ` of type ${constructorName}` : ''}. This call will be ignored.`);
  }
  storeElementPayload(element, payload) {
    const existingPayloads = this.observedElements.get(element) || [];

    // Check if the payload is already being observed for this element
    const isPayloadAlreadyObserved = existingPayloads.some(existingPayload => JSON.stringify(existingPayload) === JSON.stringify(payload));
    if (isPayloadAlreadyObserved) {
      return;
    }

    // Store the new or updated payloads
    this.observedElements.set(element, [...existingPayloads, payload]);
  }
  setupElementObservation(element, delay) {
    // Get all relevant delays from plugins and the custom delay
    const pluginDelays = this.pluginsWithCustomComponentViewThreshold.map(plugin => plugin.getComponentViewTrackingThreshold());

    // Ensure we only observe each delay once
    const uniqueDelays = Array.from(new Set([...pluginDelays, delay]));

    // Set up observation for each delay
    uniqueDelays.forEach(delay => {
      this.elementSeenObserver.observe(element, {
        delay
      });
    });
  }
  /**
   * Helper method to extract changes state from profile state and notify callback
   * @private
   */
  notifyChangesCallback(cb, profileState) {
    if (profileState.status === 'loading') {
      cb({
        status: 'loading',
        changes: null,
        error: null
      });
    } else if (profileState.status === 'error') {
      cb({
        status: 'error',
        changes: null,
        error: profileState.error
      });
    } else {
      cb({
        status: 'success',
        changes: profileState.changes,
        error: null
      });
    }
  }

  // Always throws, never returns
  handleMethodError(error, method) {
    logger.error(error);
    if (error instanceof RangeError) {
      throw new Error(`[Validation Error] "${method}" was called with invalid params. Could not validate due to "RangeError: Maximum call stack size exceeded". This can be caused by passing a cyclic data structure as a parameter. Refrain from passing a cyclic data structure or sanitize it beforehand.`);
    }
    throw error;
  }
  get profileState() {
    return this._profileState;
  }
  buildOptions(options = {}) {
    return Object.assign({}, options);
  }
  registerWindowHandlers() {
    if (typeof window !== 'undefined') {
      window.ninetailed = Object.assign({}, window.ninetailed, {
        page: this.pageAsWindowHandler.bind(this),
        track: this.trackAsWindowHandler.bind(this),
        identify: this.identifyAsWindowHandler.bind(this),
        reset: this.reset.bind(this),
        debug: this.debug.bind(this),
        profile: this.profileState.profile
      });
    }
  }
}

const selectVariant = (baseline, variants, {
  status,
  profile,
  error
}, options = {
  holdout: -1
}) => {
  if (status === 'loading') {
    return {
      loading: true,
      variant: Object.assign({}, baseline, {
        id: 'baseline',
        audience: {
          id: 'baseline'
        }
      }),
      audience: {
        id: 'baseline'
      },
      isPersonalized: false,
      error: null
    };
  }
  if (status === 'error') {
    return {
      loading: false,
      variant: Object.assign({}, baseline, {
        id: 'baseline',
        audience: {
          id: 'baseline'
        }
      }),
      audience: {
        id: 'baseline'
      },
      isPersonalized: false,
      error: error
    };
  }
  const variant = variants.find(variant => {
    var _profile$audiences, _variant$audience;
    return profile == null || (_profile$audiences = profile.audiences) == null ? void 0 : _profile$audiences.includes((_variant$audience = variant.audience) == null ? void 0 : _variant$audience.id);
  });
  if (variant) {
    if (options != null && options.holdout || -1 > ((profile == null ? void 0 : profile.random) || 0)) {
      return {
        loading: false,
        variant: Object.assign({}, baseline, {
          audience: {
            id: 'baseline'
          }
        }),
        audience: Object.assign({}, variant.audience, {
          id: variant.audience.id
        }),
        isPersonalized: false,
        error: null
      };
    }
    return {
      loading: false,
      variant,
      audience: Object.assign({}, variant.audience, {
        id: variant.audience.id
      }),
      isPersonalized: true,
      error: null
    };
  }

  /**
   * There was no matching audience found.
   */
  return {
    loading: false,
    variant: Object.assign({}, baseline, {
      id: 'baseline',
      audience: {
        id: 'baseline'
      }
    }),
    audience: {
      id: 'baseline'
    },
    isPersonalized: false,
    error: null
  };
};

export { ANONYMOUS_ID, CHANGES_FALLBACK_CACHE, COMPONENT, COMPONENT_START, CONSENT, DEBUG_FLAG, EMPTY_MERGE_ID, EXPERIENCES_FALLBACK_CACHE, EventBuilder, HAS_SEEN_STICKY_COMPONENT, LEGACY_ANONYMOUS_ID, Ninetailed, NinetailedCorePlugin, OnChangeEmitter, PAGE_HIDDEN, PLUGIN_NAME, PROFILE_CHANGE, PROFILE_FALLBACK_CACHE, PROFILE_RESET, SET_ENABLED_FEATURES, buildClientNinetailedRequestContext, decodeExperienceVariantsMap, makeChangesModificationMiddleware, makeExperienceSelectMiddleware, selectPluginsHavingExperienceSelectionMiddleware, selectPluginsHavingOnChangeEmitter, selectVariant };
